import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, Trash2, Key, CreditCard, XCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLANS = [
  { id: 'gratuito', label: 'Gratuito', price: 'Gratis', priceNum: 0, limit: '10 cupons/mes' },
  { id: 'premium', label: 'Premium', price: 'R$ 150/mês', priceNum: 150, limit: '30 cupons/mês' },
  { id: 'platinum', label: 'Platinum', price: 'R$ 300/mês', priceNum: 300, limit: '50 cupons/mês' },
  { id: 'black', label: 'Black', price: 'R$ 750/mês', priceNum: 750, limit: '80 cupons/mês' },
];

export default function EditProfilePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [subInfo, setSubInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    whatsapp: '', company_name: '', contact_name: '', city: '', commercial_address: '', plan: 'gratuito'
  });
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [accessKey, setAccessKey] = useState('');
  const [activating, setActivating] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const token = localStorage.getItem('session_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    Promise.all([
      axios.get(`${BACKEND_URL}/api/auth/me`, { headers }),
      axios.get(`${BACKEND_URL}/api/subscription/info`, { headers }).catch(() => null)
    ]).then(([userRes, subRes]) => {
      setUser(userRes.data);
      setSubInfo(subRes?.data || null);
      setForm({
        whatsapp: userRes.data.whatsapp || '',
        company_name: userRes.data.company_name || '',
        contact_name: userRes.data.contact_name || '',
        city: userRes.data.city || '',
        commercial_address: userRes.data.commercial_address || '',
        plan: userRes.data.plan || 'gratuito'
      });
    }).catch(() => toast.error('Erro ao carregar perfil'))
      .finally(() => setLoading(false));
  }, []);

  const handleDeleteAccount = async () => {
    if (!confirm('Tem certeza que deseja excluir sua conta? Esta acao e irreversivel e todos os seus cupons serao desativados.')) return;
    if (!confirm('ULTIMA CONFIRMACAO: Excluir conta permanentemente?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/auth/account`, { headers });
      localStorage.removeItem('session_token');
      toast.success('Conta excluida com sucesso');
      navigate('/');
    } catch (error) {
      toast.error('Erro ao excluir conta');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/auth/profile`, form, { headers });
      toast.success('Perfil atualizado com sucesso!');
      navigate('/merchant');
    } catch (error) {
      toast.error('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleUpgradePlan = (planId) => {
    if (planId === 'gratuito') return;
    setSelectedPlan(planId);
    setAccessKey('');
    setShowKeyDialog(true);
  };

  const handleStripeCheckout = async () => {
    if (!selectedPlan) return;
    setActivating(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/subscription/checkout`, {
        plan: selectedPlan,
        access_key: accessKey || '',
        origin_url: window.location.origin
      }, { headers });

      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar pagamento');
    } finally {
      setActivating(false);
    }
  };

  const handleActivateKey = async () => {
    if (!accessKey.trim()) {
      toast.error('Digite a chave de acesso');
      return;
    }
    // Use Stripe checkout with the key for discount
    await handleStripeCheckout();
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/subscription/cancel`, {}, { headers });
      toast.success(res.data.message);
      setShowCancelDialog(false);
      // Refresh data
      const userRes = await axios.get(`${BACKEND_URL}/api/auth/me`, { headers });
      setUser(userRes.data);
      setForm(prev => ({ ...prev, plan: 'gratuito' }));
      setSubInfo(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar');
    } finally {
      setCancelling(false);
    }
  };

  // Poll payment status after returning from Stripe
  const pollPayment = useCallback(async (sessionId) => {
    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      if (attempts >= maxAttempts) return;
      try {
        const res = await axios.get(`${BACKEND_URL}/api/subscription/status/${sessionId}`, { headers });
        if (res.data.payment_status === 'paid') {
          toast.success(`Plano ${res.data.plan} ativado com sucesso!`);
          const userRes = await axios.get(`${BACKEND_URL}/api/auth/me`, { headers });
          setUser(userRes.data);
          setForm(prev => ({ ...prev, plan: res.data.plan }));
          navigate('/merchant', { replace: true });
          return;
        }
        if (res.data.status === 'expired') {
          toast.error('Sessao de pagamento expirou');
          return;
        }
        attempts++;
        setTimeout(poll, 2000);
      } catch {
        attempts++;
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [headers, navigate]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPayment(sessionId);
    }
  }, [searchParams, pollPayment]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-slate-500"></div>
      </div>
    );
  }

  const currentPlan = PLANS.find(p => p.id === form.plan);
  const isCancelled = user?.cancelled || subInfo?.cancelled;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button variant="ghost" onClick={() => navigate('/merchant')} className="mb-6" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Voltar
        </Button>

        <Card className="hero-card mb-6">
          <CardHeader><CardTitle className="text-2xl">Editar Perfil</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-5">
              <div>
                <Label className="text-white">WhatsApp</Label>
                <Input value={form.whatsapp} onChange={e => setForm({ ...form, whatsapp: e.target.value })}
                  className="mt-1" data-testid="edit-whatsapp" required />
              </div>
              <div>
                <Label className="text-white">Nome da Empresa</Label>
                <Input value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })}
                  className="mt-1" data-testid="edit-company" required />
              </div>
              <div>
                <Label className="text-white">Nome do Contato</Label>
                <Input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })}
                  className="mt-1" data-testid="edit-contact" required />
              </div>
              <div>
                <Label className="text-white">Cidade</Label>
                <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
                  className="mt-1" data-testid="edit-city" required />
              </div>
              <div>
                <Label className="text-white">Endereco Comercial</Label>
                <Input value={form.commercial_address} onChange={e => setForm({ ...form, commercial_address: e.target.value })}
                  className="mt-1" data-testid="edit-address" required />
              </div>
              <Button type="submit" disabled={saving} className="w-full bg-white/20 hover:bg-white/30 text-white font-bold py-3 border border-white/30"
                data-testid="save-profile-btn">
                {saving ? 'Salvando...' : 'Salvar Alteracoes'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Cancellation Warning */}
        {isCancelled && (
          <Card className="bg-red-50 border-2 border-red-300 mb-6" data-testid="cancelled-warning">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
                <div>
                  <p className="font-bold text-red-700">Assinatura Cancelada</p>
                  <p className="text-sm text-red-600">Sua conta foi rebaixada para o plano Gratuito e nao pode ser reativada. Para assinar novamente, exclua sua conta e crie uma nova.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Plan Management */}
        <Card className="bg-white border border-slate-200">
          <CardHeader>
            <CardTitle className="text-xl text-slate-900">Sua Assinatura</CardTitle>
            <p className="text-sm text-slate-600">
              Plano atual: <strong>{currentPlan?.label}</strong> ({currentPlan?.limit})
              {subInfo?.next_billing_date && (
                <span className="ml-2 text-xs text-slate-400">
                  Proxima cobranca: {new Date(subInfo.next_billing_date).toLocaleDateString('pt-BR')}
                </span>
              )}
            </p>
          </CardHeader>
          <CardContent>
            {!isCancelled ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  {PLANS.filter(p => p.id !== 'gratuito').map(p => (
                    <div key={p.id} className={`border-2 rounded-md p-3 transition-all ${form.plan === p.id ? 'border-slate-500 bg-slate-50' : 'border-slate-200'}`}>
                      <h3 className="font-bold text-slate-900">{p.label}</h3>
                      <p className="text-lg font-black text-slate-900">{p.price}</p>
                      <p className="text-xs text-slate-500 mb-2">{p.limit}</p>
                      {form.plan !== p.id && (
                        <Button size="sm" onClick={() => handleUpgradePlan(p.id)}
                          className="bg-slate-700 hover:bg-slate-800 text-white text-xs w-full"
                          data-testid={`switch-plan-${p.id}`}>
                          <CreditCard className="h-3 w-3 mr-1" /> Assinar
                        </Button>
                      )}
                      {form.plan === p.id && <Badge className="bg-green-500 text-white text-xs border-0">Ativo</Badge>}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-3">
                  Pagamento mensal recorrente no cartao de credito. Tem uma chave de influenciador? Insira ao selecionar o plano.
                </p>

                {/* Cancel subscription button */}
                {form.plan !== 'gratuito' && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <Button variant="outline" size="sm" onClick={() => setShowCancelDialog(true)}
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      data-testid="cancel-subscription-btn">
                      <XCircle className="h-4 w-4 mr-2" /> Cancelar Assinatura
                    </Button>
                    <p className="text-xs text-red-400 mt-1">
                      Ao cancelar, sua conta sera rebaixada para Gratuito sem cupons e nao podera ser reativada.
                    </p>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-500">
                Assinatura cancelada. Para assinar novamente, exclua sua conta e crie uma nova.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Plan Checkout Dialog */}
        <Dialog open={showKeyDialog} onOpenChange={setShowKeyDialog}>
          <DialogContent className="!bg-white max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold text-slate-900">
                Assinar Plano {PLANS.find(p => p.id === selectedPlan)?.label}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="bg-slate-50 rounded-md p-3">
                <p className="text-sm text-slate-600">Valor mensal:</p>
                <p className="text-2xl font-bold text-slate-900">
                  {PLANS.find(p => p.id === selectedPlan)?.price}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-600 mb-2">Tem uma chave de desconto? (opcional)</p>
                <Input value={accessKey} onChange={e => setAccessKey(e.target.value.toUpperCase())}
                  placeholder="Ex: VIP-PRE-A1B2C3" className="font-mono text-center tracking-wider"
                  data-testid="access-key-input" />
              </div>
              <Button onClick={handleStripeCheckout} disabled={activating}
                className="w-full bg-slate-700 hover:bg-slate-800 text-white font-bold"
                data-testid="checkout-btn">
                <CreditCard className="h-4 w-4 mr-2" />
                {activating ? 'Processando...' : 'Pagar com Cartao'}
              </Button>
              <p className="text-xs text-slate-400 text-center">
                Pagamento seguro via Stripe. Cobranca mensal recorrente.
              </p>
            </div>
          </DialogContent>
        </Dialog>

        {/* Cancel Subscription Dialog */}
        <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <DialogContent className="!bg-white max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold text-red-600">Cancelar Assinatura</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-700 font-semibold mb-2">Atencao! Ao cancelar:</p>
                <ul className="text-sm text-red-600 space-y-1 list-disc list-inside">
                  <li>Sua conta sera rebaixada para o plano Gratuito</li>
                  <li>Todos os seus cupons serao desativados</li>
                  <li>A conta <strong>nao podera ser reativada</strong></li>
                  <li>Para usar o ClubdoCupom novamente, sera necessario excluir a conta e criar uma nova</li>
                </ul>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => setShowCancelDialog(false)}
                  className="border-slate-300" data-testid="cancel-dialog-back-btn">
                  Voltar
                </Button>
                <Button size="sm" onClick={handleCancelSubscription} disabled={cancelling}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-cancel-btn">
                  {cancelling ? 'Cancelando...' : 'Confirmar Cancelamento'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Account */}
        <Card className="bg-white border-2 border-red-200 mt-6">
          <CardHeader>
            <CardTitle className="text-lg text-red-600 flex items-center gap-2"><Trash2 className="h-5 w-5" /> Zona de Perigo</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-4">Ao excluir sua conta, todos os seus cupons serao desativados e seus dados removidos permanentemente.</p>
            <Button variant="outline" onClick={handleDeleteAccount}
              className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
              data-testid="delete-account-btn">
              <Trash2 className="h-4 w-4 mr-2" /> Excluir Minha Conta
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
